/**
 * Draft persistence for the custom character builder.
 *
 * The builder modal is unmounted whenever the user closes it, so any in-progress
 * edits would otherwise be lost. We mirror the editable fields to localStorage on
 * every change and restore them on the next open, letting a user who closes the
 * modal mid-way resume where they left off. The draft is cleared only when a
 * character is successfully saved — closing or cancelling deliberately keeps it.
 */
import type {
  PersonalityTraits
} from '../services/api'

export interface CustomCharacterDraft {
  name: string
  bio: string
  traits: PersonalityTraits
  selectedVoice: string
}

const DRAFT_KEY_PREFIX = 'catch_custom_character_draft'

// Create and edit sessions get separate keys so an in-progress "create" never
// leaks into an "edit" of an existing character (and vice versa).
function draftKey(characterId: number | null | undefined): string {
  return characterId != null
    ? `${DRAFT_KEY_PREFIX}:edit:${characterId}`
    : `${DRAFT_KEY_PREFIX}:create`
}

// Keyed as a Record so that adding a trait to PersonalityTraits without listing
// it here is a compile error — a plain array wouldn't enforce exhaustiveness,
// and the validator would silently accept a draft missing the new trait.
const TRAIT_KEY_PRESENCE: Record<keyof PersonalityTraits, true> = {
  openness: true,
  conscientiousness: true,
  extraversion: true,
  agreeableness: true,
  sensitivity: true,
}
const TRAIT_KEYS = Object.keys(TRAIT_KEY_PRESENCE) as (keyof PersonalityTraits)[]

export function isPersonalityTraits(value: unknown): value is PersonalityTraits {
  if (typeof value !== 'object' || value === null) return false
  const record = value as Record<string, unknown>
  return TRAIT_KEYS.every((key) => typeof record[key] === 'number')
}

function isCustomCharacterDraft(value: unknown): value is CustomCharacterDraft {
  if (typeof value !== 'object' || value === null) return false
  const record = value as Record<string, unknown>
  return (
    typeof record['name'] === 'string'
    && typeof record['bio'] === 'string'
    && typeof record['selectedVoice'] === 'string'
    && isPersonalityTraits(record['traits'])
  )
}

/** Read a saved draft for this create/edit session, or null if none/invalid. */
export function loadCustomCharacterDraft(
  characterId: number | null | undefined,
): CustomCharacterDraft | null {
  try {
    const raw = localStorage.getItem(draftKey(characterId))
    if (!raw) return null
    const parsed: unknown = JSON.parse(raw)
    return isCustomCharacterDraft(parsed) ? parsed : null
  } catch {
    // Malformed JSON or storage unavailable — treat as no draft.
    return null
  }
}

/** Persist the in-progress builder fields for this create/edit session. */
export function saveCustomCharacterDraft(
  characterId: number | null | undefined,
  draft: CustomCharacterDraft,
): void {
  try {
    localStorage.setItem(draftKey(characterId), JSON.stringify(draft))
  } catch {
    // Storage can be unavailable (Safari private mode, quota exceeded).
    // Drafting is a best-effort convenience, so failures are ignored.
  }
}

/** Remove the saved draft once the character has been saved. */
export function clearCustomCharacterDraft(
  characterId: number | null | undefined,
): void {
  try {
    localStorage.removeItem(draftKey(characterId))
  } catch {
    // Ignore — see saveCustomCharacterDraft.
  }
}
